import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { CREATE_TELEGRAM_HANDOFF } from '../graphql/mutations';

interface CreateTelegramHandoffResult {
  createTelegramHandoff: {
    token: string;
    botUrl: string;
  };
}

/**
 * Opens the Telegram bot using a handoff deep-link. Fetches a short-lived token
 * bound to the current web session so the bot can merge both identities on /start.
 *
 * Falls back to the plain bot URL if the mutation fails (e.g. network issues or
 * company has no bot configured) — user still gets to Telegram.
 */
export function useTelegramHandoff(fallbackBotUrl?: string): (event?: React.MouseEvent) => Promise<void> {
  const [createHandoff] = useMutation<CreateTelegramHandoffResult>(CREATE_TELEGRAM_HANDOFF);

  return useCallback(
    async (event?: React.MouseEvent) => {
      event?.preventDefault();

      try {
        const { data } = await createHandoff();
        const url = data?.createTelegramHandoff?.botUrl ?? fallbackBotUrl;
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer');
          return;
        }
      } catch (error) {
        console.warn('[WebChatWidget] Telegram handoff failed, using fallback URL', error);
      }

      if (fallbackBotUrl) {
        window.open(fallbackBotUrl, '_blank', 'noopener,noreferrer');
      }
    },
    [createHandoff, fallbackBotUrl],
  );
}
